import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DisableFormDirective } from '../../../../../@core/directives/disable-form.directive';
import { EditLinks } from '../../../../../@core/constants/edit-links';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { findFieldByKey } from '../../../../../common/utilities';
import { Address, Document, DocumentDirection, DocumentStatusType } from '../../../../../generated/generated-types';
import { AddressDropDownComponent } from '../../../../../modules/crm/modules/company-drop-down/company-drop-down/address-drop-down.component';
import { AddressService } from '../../../../crm/services/address.service';
import { WidgetEditGenericDocumentComponent } from '../../widget-edit-generic-document/widget-edit-generic-document.component';
import { documentPostalAddressFallbackFields } from './fallback-fields';

@Component({
  selector: 'app-formly-document-postal-address-widget',
  templateUrl: './formly-document-postal-address-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    DisableFormDirective,
    FormlyModule,
    ReactiveFormsModule,
    FormsModule,
    AddressDropDownComponent,
    TranslatePipe,
  ]
})
export class FormlyDocumentPostalAddressWidgetComponent extends FieldType<FieldTypeConfig> implements AfterViewInit {
  destroyRef = inject(DestroyRef);

  DocumentStatusType = DocumentStatusType;
  DocumentDirectionType = DocumentDirection;

  postalAddressForm = new FormGroup({
    documentPostalAddress: new FormControl(null)
  });

  fallbackFields: FormlyFieldConfig[] = [];

  addressDropDown = viewChild(AddressDropDownComponent);

  dataTestId = signal(null);

  constructor(
    private i18nService: I18nService,
    private addressService: AddressService,
    private cdRef: ChangeDetectorRef) {
    super();
  }

  ngAfterViewInit(): void {
    this.field.props.icon = "fa-solid fa-address-book";
    this.fallbackFields = documentPostalAddressFallbackFields();
    //TODO load fields from server, we can extends our base class to fieldtype

    //this will be triggered by the fallback form and update the formControl
    // Subscribe to changes in the nested postal address form
    this.postalAddressForm?.valueChanges.pipe(
      debounceTime(100),// Just to avoid too many calls to the parent form
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      // Set the value of the parent form control with the current form value.
      // If you add this line, you are sometimes sending null values to the parent form. Witch makes a change in the logic of the parent form. in this.zone.runOutsideAngular WidgetEditGenericDocumentComponent
      // this.formControl.patchValue({ documentPostalAddress: value.documentPostalAddress }, { emitEvent: true });
      this.formControl.markAsDirty();
      this.formControl.updateValueAndValidity({ onlySelf: false });

      // // Validate and pass errors
      let errors: ValidationErrors = Object.keys(this.postalAddressForm.controls).reduce((acc, key) => {
        const control = this.postalAddressForm.controls[key];
        if (control.errors) {
          const errorKey = Object.keys(control.errors)[0];
          acc[errorKey] = control.errors[errorKey];
        }
        return acc;
      }, {});

      if (Object.keys(errors).length > 0) {
        this.formControl.setErrors(errors);
      }

      this.cdRef.markForCheck();  // ✅ Optimized (instead of detectChanges)
    });

    if (this.props?.attributes?.['data-testid']) {
      this.addressDropDown().externalDataTestId.set(true);
      this.addressDropDown().dataTestId.set(this.props.attributes['data-testid'].toString());
    }

    // Adjust visibility based on document direction
    let deliveryRelevant = this.options.formState?.detailComponent?.docDef()?.deliveryRelevant;
    if (deliveryRelevant) {
      const postalAddressTypeField = findFieldByKey(this.fallbackFields, 'postalAddressType');
      if (postalAddressTypeField) postalAddressTypeField.hide = false;
      // const vatIdField = findFieldByKey(this.fallbackFields, 'vatId');
      // if (vatIdField) vatIdField.hide = false;
    }

    // Register inner fallback fields so document-level configs can target nested postal keys.
    (this.formState?.detailComponent as WidgetEditGenericDocumentComponent | undefined)?.registerPostalInnerFieldConfigApplier(
      'documentPostalAddress',
      () => this.fallbackFields,
    );

    this.cdRef.markForCheck();  // ✅ Optimized (instead of detectChanges)
  }

  /**
   * @description passes addressSelection into the parent detailComponent 
   * @important handle it through formControl is incorrect since address is not part of this - also didnt trigger everytime
   * @param address 
   */
  async selectedAddressChanged(address: Address | Address[]) {
    if (!this.formState?.detailComponent) {
      return; // Early exit if no detailComponent is available, avoids unnecessary changes detection actions
    }

    if (Array.isArray(address)) {
      address = address[address.length - 1] as Address;
    }

    const widget = this.formState.detailComponent as WidgetEditGenericDocumentComponent;
    await widget.addressChanged(address);
    this.cdRef.detectChanges();
  }


  getDetailUrl(address: Address) {
    return EditLinks.ADDRESS + address.id;
  }

  showCreditor() {  //takes about 200ms to load documentDefinition

    if (!(this.model as Document)?.documentDefinition?.documentDirection) {
      return false;
    }
    let showCreditor = !((this.model as Document)?.documentDefinition?.documentDirection == DocumentDirection.OUT);
    return showCreditor;

  }

  showDebitor() {

    if (!(this.model as Document)?.documentDefinition?.documentDirection) {
      return false;
    }
    let showDebitor = !((this.model as Document).documentDefinition?.documentDirection == DocumentDirection.IN);
    return showDebitor;

  }
}
